import { createAsyncThunk } from '@reduxjs/toolkit';

import { API } from '@/shared/api';
import { getIsUserLeftProjectPage } from '@/shared/lib/location';
import { runCoordinatedSegmentWrite } from '@/shared/lib/transcriptVersionWriteLock';
import { TServerSegment, TUpdatingSegmentData } from '@/shared/types/Editor';
import { Project } from '@/shared/types/Project';

import { RootState } from '../../index';
import { editorOperationAllowsMutation, TEditorOperationToken } from './editorOperation';
import { applySavedSegments, revertFailedSegments } from './segmentSaveHelpers';

type UpdateSegments = {
  changedData: TUpdatingSegmentData[];
  operationToken?: TEditorOperationToken;
  project?: Project;
};

const BATCH_SIZE = 50;

/**
 * Разбивает массив на батчи указанного размера
 * @param array - исходный массив
 * @param batchSize - размер батча
 * @returns массив батчей
 */
const chunkArray = <T>(array: T[], batchSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    chunks.push(array.slice(i, i + batchSize));
  }
  return chunks;
};

export const updateSegments = createAsyncThunk<void, UpdateSegments, { state: RootState }>(
  'editor/updateSegments',
  async ({ changedData, operationToken, project }: UpdateSegments, { dispatch, getState }) => {
    if (!editorOperationAllowsMutation(getState(), operationToken)) {
      return;
    }

    if (!project) {
      return;
    }

    const transcriptEpoch = getState().editor.transcriptReplacedEpoch;
    const ownsResponse = () =>
      !getIsUserLeftProjectPage(project.id) &&
      getState().editor.transcriptReplacedEpoch === transcriptEpoch &&
      (!operationToken || editorOperationAllowsMutation(getState(), operationToken));

    await runCoordinatedSegmentWrite(
      project.id,
      async () => {
        if (!ownsResponse()) {
          return false;
        }

        try {
          // Разбиваем данные на батчи для предотвращения ошибки 413 "Payload Too Large"
          const batches = chunkArray(changedData, BATCH_SIZE);

          // Обрабатываем все батчи параллельно и объединяем результаты
          const batchResults = await Promise.all(
            batches.map((batch) => API.updateSegments(project.id, batch).then(({ segments }) => segments)),
          );
          const allUpdatedSegments: TServerSegment[] = batchResults.flat();

          if (!allUpdatedSegments.length) {
            throw new Error();
          }

          if (!ownsResponse() || getState().editor.historyApplying) {
            return false;
          }

          applySavedSegments(project.id, allUpdatedSegments, dispatch);
          return true;
        } catch {
          if (!ownsResponse() || getState().editor.historyApplying) {
            return false;
          }

          revertFailedSegments(changedData, { dispatch, getState });
          return false;
        }
      },
      { queueScope: getState, segmentIds: changedData.map(({ id }) => id) },
    );
  },
);
